import { useEffect, useState, type RefObject } from "react";
import type { Map as MapboxMap } from "mapbox-gl";
import "./Finance.css";
import { getEnvParam, type TileEnv } from "@/env";

interface FinanceTokensViewProps {
    map: RefObject<MapboxMap | undefined>;
    env: TileEnv;
}

function FinanceTokensView(props: FinanceTokensViewProps) {
    const [highlightPredicted, setHighlightPredicted] = useState(false);

    useEffect(() => {
        const map = props.map.current;
        if (!map) return;

        if (map.getSource("mvt-tiles")) {
            if (map.getLayer("tiles")) map.removeLayer("tiles");
            if (map.getLayer("tiles-values")) map.removeLayer("tiles-values");
            map.removeSource("mvt-tiles");
        }

        map.addSource("mvt-tiles", {
            type: "vector",
            tiles: [
                `${process.env.BUN_PUBLIC_TILE_SERVER}/api/tiles/finance/tokens/{z}/{x}/{y}?${getEnvParam()}`,
            ],
        });

        map.addLayer({
            id: "tiles",
            type: "fill",
            source: "mvt-tiles",
            "source-layer": "values",
            filter: [">", ["get", "value"], 0],
            paint: {
                "fill-color": [
                    "interpolate",
                    ["linear"],
                    ["get", "value"],
                    100_000,
                    "#2ecc71",
                    400_000,
                    "#f1c40f",
                    1_000_000,
                    "#e67e22",
                    2_000_000,
                    "#e74c3c",
                ],
                "fill-opacity": 0.3,
            },
        });

        map.addLayer({
            id: "tiles-values",
            type: "symbol",
            source: "mvt-tiles",
            "source-layer": "values",
            minzoom: 13,
            filter: [">", ["get", "value"], 0],
            paint: {
                "text-color": "#fff",
                "text-halo-color": "#000",
                "text-halo-width": 1,
            },
            layout: {
                "text-field": [
                    "case",
                    [">=", ["get", "value"], 1_000_000],
                    [
                        "concat",
                        "£",
                        [
                            "number-format",
                            ["/", ["get", "value"], 1_000_000],
                            {
                                "max-fraction-digits": 1,
                            },
                        ],
                        "M",
                    ],
                    [
                        "concat",
                        "£",
                        [
                            "number-format",
                            ["/", ["get", "value"], 1000],
                            {
                                "max-fraction-digits": 0,
                            },
                        ],
                        "k",
                    ],
                ],
                "text-font": ["Source Code Pro", "Arial Unicode MS Bold"],
                "text-size": 10,
            },
        });

        return () => {
            if (map.getSource("mvt-tiles")) {
                if (map.getLayer("tiles")) map.removeLayer("tiles");
                if (map.getLayer("tiles-values"))
                    map.removeLayer("tiles-values");
                map.removeSource("mvt-tiles");
            }
        };
    }, [props.env]);

    useEffect(() => {
        const map = props.map.current;
        if (!map || !map.getLayer("tiles")) return;
        map.setPaintProperty(
            "tiles",
            "fill-opacity",
            highlightPredicted
                ? ["case", ["==", ["get", "predicted"], true], 0.5, 0.3]
                : 0.3,
        );
    }, [highlightPredicted]);

    return (
        <div className="finance-panel">
            <div className="finance-panel__note">
                Tile shading: <strong>£100k</strong> green →{" "}
                <strong>£400k</strong> yellow → <strong>£1M</strong> orange →{" "}
                <strong>£2M+</strong> red. Aggregated from{" "}
                <code>pipeline.grid_cell_sales</code>.
            </div>
            <label className="finance-checkbox">
                <input
                    type="checkbox"
                    checked={highlightPredicted}
                    onChange={(e) => setHighlightPredicted(e.target.checked)}
                />
                Highlight predicted tiles
            </label>
        </div>
    );
}

export default FinanceTokensView;
